import { HoaFeeDto } from "@cma/shared/apis/comps/cma/generated";
import { format, FormatOptions } from "date-fns";

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Turns a PascalCase/camelCase enum value (e.g. "UpToDate") into a
// human-readable label ("Up to date").
export function humanizeEnumValue(value: string): string {
  const normalized = value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();
  return normalized.slice(0, 1).concat(normalized.slice(1).toLowerCase());
}

const normalizeUrl = (input: string | null): URL | null => {
  if (!input) return null;

  const trimmed = input.trim();
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    return new URL(withProtocol);
  } catch {
    return null;
  }
};

const convertSqftToAcres = (sqft: number): number => {
  return sqft / 43560;
};

export const textFormatter = {
  hoaFeeAndFrequency: (params: {
    legacyHoaFees?: number | null;
    hoaFeeAndFrequency?: HoaFeeDto | null;
  }): string => {
    const { hoaFeeAndFrequency, legacyHoaFees } = params;
    if (hoaFeeAndFrequency) {
      const amount = textFormatter.currency(hoaFeeAndFrequency.fee);
      return `${amount} ${hoaFeeAndFrequency.frequency}`;
    }
    return textFormatter.currency(legacyHoaFees);
  },
  currency: (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    const numberValue = typeof value === "number" ? value : parseFloat(value);
    return `$${numberValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
    })}`;
  },
  number: (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    const numberValue = typeof value === "number" ? value : parseFloat(value);
    return numberValue.toLocaleString();
  },
  transactionType: (isLease: boolean) =>
    isLease === true ? "Lease" : isLease === false ? "Sale" : "-",
  valueToText: (
    value: string | number | null | undefined,
    opts: { fallback?: string } = {},
  ) => {
    if (value === null || value === undefined) return opts.fallback || "-";
    if (typeof value === "number") return value.toString();
    if (typeof value === "string" && value.trim() === "")
      return opts.fallback || "-";
    return value;
  },
  area: (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    const numberValue = typeof value === "number" ? value : parseFloat(value);

    if (numberValue >= 40_000) {
      const acres = convertSqftToAcres(numberValue);
      return `${acres.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })} acres`;
    }

    return `${numberValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} sq ft`;
  },
  date: (
    date: string | number | Date,
    formatStr?: string,
    options?: FormatOptions,
  ) => {
    return format(date, formatStr || "MM/dd/yyyy", options);
  },
  doormanAttendance: (hasDoormanAttendance: boolean | null | undefined) => {
    if (hasDoormanAttendance === null || hasDoormanAttendance === undefined)
      return "-";
    return hasDoormanAttendance ? "Yes" : "No";
  },
  formatPhoneNumber: (phoneNumber: string | null | undefined) => {
    if (!phoneNumber) return "-";

    const cleaned = `${phoneNumber}`.replace(/\D/g, "");

    // Handle international numbers with country code
    const usMatch = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
    if (usMatch) {
      return `+1 (${usMatch[1]}) ${usMatch[2]}-${usMatch[3]}`;
    }

    // Standard US number without country code: 10 digits
    const standardMatch = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (standardMatch) {
      return `(${standardMatch[1]}) ${standardMatch[2]}-${standardMatch[3]}`;
    }

    // Other international numbers (keep original format with + if present)
    if (phoneNumber.startsWith("+") && cleaned.length > 10) {
      return `+${cleaned}`;
    }

    return phoneNumber;
  },
  formatClickableUrl: (input: string | null): string | null => {
    const url = normalizeUrl(input);
    return url ? url.href : null;
  },
  formatPrettyUrl: (input: string | null): string | null => {
    const url = normalizeUrl(input);
    return url
      ? (url.host + url.pathname + url.search + url.hash).replace(/\/$/, "")
      : null;
  },
  formatFullAddress: (address: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }): string => {
    const { addressLine1, addressLine2, city, state, zipCode } = address;
    const line1And2 = [addressLine1, addressLine2].filter(Boolean).join(", ");
    const stateAndZip = [state, zipCode].filter(Boolean).join(" ");
    return [line1And2, city, stateAndZip].filter(Boolean).join(", ");
  },
};
